import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import SafeIcon from '../common/SafeIcon';
import { 
  FiSearch, FiFilter, FiDownload, FiEye, 
  FiUpload, FiLoader, FiAlertCircle, FiTrash2, 
  FiSave, FiZap, FiUserCheck, FiX, FiUser, 
  FiChevronRight, FiBox, FiTruck, FiCheckCircle 
} from 'react-icons/fi';
import { ClaimDrawer } from '../components/claims/ClaimDrawer';
import supabase from '../supabase/supabase';
import * as XLSX from 'xlsx';

export const Claims = () => {
  const [activeTab, setActiveTab] = useState('new');
  const [claims, setClaims] = useState([]);
  const [unsavedClaims, setUnsavedClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  // Distribution State
  const [distributingAI, setDistributingAI] = useState(false);
  const [distributingManual, setDistributingManual] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [salvagersList, setSalvagersList] = useState([]);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const fileInputRef = useRef(null);
  const headerCheckboxRef = useRef(null);

  useEffect(() => {
    fetchClaims();
    const handleClickOutside = () => setContextMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  // Clear selections when switching tabs
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab]);

  const fetchClaims = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('claims_1783447171616')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch profiles to map salvager names for the "Distributed to" blurb
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles_1783455000000')
        .select('id, company_name, full_name');
        
      const profileMap = {};
      if (!profilesError && profilesData) {
        profilesData.forEach(p => {
          profileMap[p.id] = p.company_name || p.full_name || 'Unknown Salvager';
        });
      }

      // Master check for timed out claims to seamlessly auto-revert them
      const now = new Date().getTime();
      const timedOutIds = data
        .filter(c => c.status === 'Assigned' && c.allocated_at && (now - new Date(c.allocated_at).getTime() > 24 * 60 * 60 * 1000))
        .map(c => c.id);

      if (timedOutIds.length > 0) {
        await supabase
          .from('claims_1783447171616')
          .update({ status: 'Timed Out', assigned_salvager_id: null })
          .in('id', timedOutIds);
        
        // Optimistically update
        data.forEach(c => {
          if (timedOutIds.includes(c.id)) {
            c.status = 'Timed Out';
            c.assigned_salvager_id = null;
          }
        });
      }

      // Apply salvager names
      const enrichedData = data.map(c => ({
        ...c,
        salvagerName: c.assigned_salvager_id ? (profileMap[c.assigned_salvager_id] || 'Unknown') : null
      }));

      setClaims(enrichedData || []);
      setSelectedIds(new Set()); // Reset selections on refresh
    } catch (err) {
      console.error('Error fetching claims:', err);
      setError('Failed to load claims from database.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSalvagers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles_1783455000000')
        .select('*')
        .eq('role', 'SALVAGER');
      
      if (error) throw error;
      setSalvagersList(data || []);
      return data || [];
    } catch (err) {
      console.error('Error fetching salvagers:', err);
      return [];
    }
  };

  // Processing claims for tabs and display
  const allClaims = [...unsavedClaims, ...claims];

  const newCount = allClaims.filter(c => c.status === 'New' || c.status === 'Timed Out').length;
  const assignedCount = allClaims.filter(c => c.status === 'Assigned').length;
  const acceptedCount = allClaims.filter(c => c.status === 'Accepted').length;
  const rejectedCount = allClaims.filter(c => c.status === 'Declined' || c.status === 'Rejected').length;

  const displayClaims = allClaims.filter(c => {
    // 1. Tab Filtering
    if (activeTab === 'new' && c.status !== 'New' && c.status !== 'Timed Out') return false;
    if (activeTab === 'assigned' && c.status !== 'Assigned') return false;
    if (activeTab === 'accepted' && c.status !== 'Accepted') return false;
    if (activeTab === 'rejected' && c.status !== 'Declined' && c.status !== 'Rejected') return false;

    // 2. Search Filtering
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        (c.claim_number && c.claim_number.toLowerCase().includes(q)) ||
        (c.salvage_item && c.salvage_item.toLowerCase().includes(q)) ||
        (c.make && c.make.toLowerCase().includes(q)) ||
        (c.model && c.model.toLowerCase().includes(q)) ||
        (c.serial_number && c.serial_number.toLowerCase().includes(q)) ||
        (c.ber_reason && c.ber_reason.toLowerCase().includes(q)) ||
        (c.salvagerName && c.salvagerName.toLowerCase().includes(q))
      );
    }
    return true;
  });

  // Update master checkbox indeterminate state
  useEffect(() => {
    if (headerCheckboxRef.current) {
      const isIndeterminate = selectedIds.size > 0 && selectedIds.size < displayClaims.length;
      headerCheckboxRef.current.indeterminate = isIndeterminate;
    }
  }, [selectedIds, displayClaims.length]);

  const handleValidDistributionIds = () => {
    const savedIds = Array.from(selectedIds).filter(id => claims.some(c => c.id === id));
    if (savedIds.length === 0) {
      alert('Please save the selected claims to the database first before distributing.');
      return null;
    }
    if (savedIds.length !== selectedIds.size) {
      alert('Some selected claims are unsaved drafts. Only saved claims will be distributed.');
    }
    return savedIds;
  };

  const handleManualDistribute = async (salvagerId) => {
    const validIds = handleValidDistributionIds();
    if (!validIds) return;

    setDistributingManual(true);
    try {
      const { error } = await supabase
        .from('claims_1783447171616')
        .update({ assigned_salvager_id: salvagerId, status: 'Assigned' })
        .in('id', validIds);

      if (error) throw error;
      
      fetchClaims();
      setShowManualModal(false);
    } catch (err) {
      console.error('Manual distribute error:', err);
      alert('Failed to assign claims. Please try again.');
    } finally {
      setDistributingManual(false);
    }
  };

  const handleAIDistribute = async () => {
    const validIds = handleValidDistributionIds();
    if (!validIds) return;

    setDistributingAI(true);
    try {
      const salvagers = await fetchSalvagers();
      const validClaims = claims.filter(c => validIds.includes(c.id));
      
      const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
      let assignments = [];

      if (apiKey) {
        // AI Distribution Logic via OpenAI API
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            messages: [
              {
                role: "system",
                content: "You are an AI distribution routing agent. Assign the given claims to the provided salvagers based on tags. Match make/model/item to tags. If multiple salvagers match, distribute equally. Output a JSON object with a single key 'assignments' containing an array of objects: {\"claim_id\": \"uuid\", \"salvager_id\": \"uuid\"}."
              },
              {
                role: "user",
                content: JSON.stringify({
                  claims: validClaims.map(c => ({ claim_id: c.id, make: c.make, model: c.model, item: c.salvage_item })),
                  salvagers: salvagers.map(s => ({ salvager_id: s.id, tags: s.tags }))
                })
              }
            ],
            response_format: { type: "json_object" }
          })
        });

        if (!response.ok) throw new Error('API Request Failed');
        
        const resData = await response.json();
        const content = JSON.parse(resData.choices[0].message.content);
        assignments = content.assignments || [];
      } else {
        // Fallback local Matching Algorithm if API key is not present
        console.log("No AI key found, using local intelligent distribution logic");
        const salvagerCounts = {};
        salvagers.forEach(s => salvagerCounts[s.id] = 0);

        assignments = validClaims.map(claim => {
          const claimText = `${claim.make} ${claim.model} ${claim.salvage_item}`.toLowerCase();
          
          let bestScore = -1;
          let bestMatches = [];

          salvagers.forEach(s => {
            let score = 0;
            (s.tags || []).forEach(tag => {
              const t = tag.toLowerCase();
              if (claimText.includes(t)) score += 2;
              else if (t.includes(claim.make?.toLowerCase() || '---')) score += 1;
            });

            if (score > bestScore) {
              bestScore = score;
              bestMatches = [s];
            } else if (score === bestScore) {
              bestMatches.push(s);
            }
          });

          // Sort by lowest load to distribute equally among best matches
          let selected = bestMatches.reduce((prev, curr) => 
            salvagerCounts[curr.id] < salvagerCounts[prev.id] ? curr : prev
          , bestMatches[0]);

          if (!selected && salvagers.length > 0) {
            // Ultimate fallback to random least loaded
            selected = salvagers.reduce((prev, curr) => 
              salvagerCounts[curr.id] < salvagerCounts[prev.id] ? curr : prev
            , salvagers[0]);
          }

          if (selected) {
            salvagerCounts[selected.id]++;
            return { claim_id: claim.id, salvager_id: selected.id };
          }
          return null;
        }).filter(Boolean);
      }

      // Group by salvager to reduce database calls
      const bySalvager = {};
      assignments.forEach(a => {
        if (!bySalvager[a.salvager_id]) bySalvager[a.salvager_id] = [];
        bySalvager[a.salvager_id].push(a.claim_id);
      });

      // Apply assignments to database
      for (const [salvagerId, claimIds] of Object.entries(bySalvager)) {
        await supabase
          .from('claims_1783447171616')
          .update({ assigned_salvager_id: salvagerId, status: 'Assigned' })
          .in('id', claimIds);
      }

      alert(`Successfully distributed ${assignments.length} claims.`);
      fetchClaims();
    } catch (err) {
      console.error('AI Distribution error:', err);
      alert('AI Distribution failed. Please check your API Key or try again.');
    } finally {
      setDistributingAI(false);
    }
  };

  const handleViewClaim = (claim) => {
    setSelectedClaim(claim);
    setIsDrawerOpen(true);
  };

  // MULTI-SELECT LOGIC
  const toggleSelection = (e, claim) => {
    e.stopPropagation();
    const key = claim.id || claim.claim_number;
    const newSet = new Set(selectedIds);
    if (newSet.has(key)) {
      newSet.delete(key);
    } else {
      newSet.add(key);
    }
    setSelectedIds(newSet);
  };

  const toggleAll = () => {
    if (selectedIds.size === displayClaims.length && displayClaims.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayClaims.map(c => c.id || c.claim_number)));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} selected claims? This action cannot be undone.`)) return;

    const savedIdsToDelete = [];
    const unsavedNumbersToDelete = [];

    displayClaims.forEach(c => {
      const key = c.id || c.claim_number;
      if (selectedIds.has(key)) {
        if (c._isUnsaved) unsavedNumbersToDelete.push(c.claim_number);
        else savedIdsToDelete.push(c.id);
      }
    });

    try {
      if (savedIdsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('claims_1783447171616')
          .delete()
          .in('id', savedIdsToDelete);
        
        if (deleteError) throw deleteError;
      }

      if (savedIdsToDelete.length > 0) {
        setClaims(prev => prev.filter(c => !savedIdsToDelete.includes(c.id)));
      }
      if (unsavedNumbersToDelete.length > 0) {
        setUnsavedClaims(prev => prev.filter(c => !unsavedNumbersToDelete.includes(c.claim_number)));
      }

      setSelectedIds(new Set());
      setContextMenu(null);
    } catch (err) {
      console.error('Bulk delete error:', err);
      alert('Failed to delete some claims. Please try again.');
    }
  };

  const handleDeleteClaim = async (claim) => {
    if (!window.confirm('Are you sure you want to delete this claim? This action cannot be undone.')) return;

    // If it's an unsaved draft, just remove it from state
    if (claim._isUnsaved) {
      setUnsavedClaims(prev => prev.filter(c => c.claim_number !== claim.claim_number));
      setContextMenu(null);
      removeFromSelection(claim.claim_number);
      return;
    }

    try {
      const { error } = await supabase
        .from('claims_1783447171616')
        .delete()
        .eq('id', claim.id);

      if (error) throw error;
      setClaims(prev => prev.filter(c => c.id !== claim.id));
      setContextMenu(null);
      removeFromSelection(claim.id);
    } catch (err) {
      console.error('Error deleting claim:', err);
      alert('Failed to delete claim. Please try again.');
    }
  };

  const removeFromSelection = (key) => {
    if (selectedIds.has(key)) {
      const newSet = new Set(selectedIds);
      newSet.delete(key);
      setSelectedIds(newSet);
    }
  };

  const handleContextMenu = (e, claim) => {
    e.preventDefault();
    setContextMenu({ x: e.pageX, y: e.pageY, claim: claim });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

        if (!jsonData || jsonData.length === 0) {
          throw new Error('The Excel file appears to be empty or has no recognizable data.');
        }

        const existingSignatures = new Set(
          claims.map(c => {
            const baseNum = c.claim_number.replace(/-\d+$/, '');
            return `${baseNum}-${c.salvage_item}-${c.make}-${c.model}-${c.serial_number}`.toLowerCase();
          })
        );
        
        const claimCounts = {};
        claims.forEach(c => {
          const baseNum = c.claim_number.replace(/-\d+$/, '');
          claimCounts[baseNum] = (claimCounts[baseNum] || 0) + 1;
        });

        const seenItemsInImport = new Set();

        const mappedClaims = jsonData.map((row, index) => {
          const isRowEmpty = Object.values(row).every(val => String(val).trim() === '');
          if (isRowEmpty) return null;

          const keys = Object.keys(row);
          const findVal = (possibleKeys) => {
            let foundKey = keys.find(k => {
              const cleanK = k.toLowerCase().replace(/[^a-z0-9]/g, '');
              return possibleKeys.some(pk => cleanK === pk.toLowerCase().replace(/[^a-z0-9]/g, ''));
            });
            if (!foundKey) {
              foundKey = keys.find(k => {
                const cleanK = k.toLowerCase().replace(/[^a-z0-9]/g, '');
                return possibleKeys.some(pk => cleanK.includes(pk.toLowerCase().replace(/[^a-z0-9]/g, '')));
              });
            }
            return foundKey && row[foundKey] !== undefined ? String(row[foundKey]).trim() : '';
          };

          let baseClaimNum = findVal(['claimnr', 'claimnumber', 'claimno', 'claim', 'reference', 'job', 'case']);
          if (!baseClaimNum) {
            baseClaimNum = `IMP-ROW${index + 1}`;
          }

          const salvage_item = findVal(['item', 'salvageitem', 'product', 'device', 'type', 'description', 'goods', 'commodity', 'equipment', 'asset']) || 'Unknown';
          const make = findVal(['make', 'brand', 'manufacturer']) || 'Unknown';
          const model = findVal(['model', 'modelno', 'modelnumber']) || 'Unknown';
          const serial = findVal(['serial', 'imei', 'sn']) || 'N/A';

          const itemSignature = `${baseClaimNum}-${salvage_item}-${make}-${model}-${serial}`.toLowerCase();
          
          if (seenItemsInImport.has(itemSignature) || existingSignatures.has(itemSignature)) {
            return null;
          }
          seenItemsInImport.add(itemSignature);

          claimCounts[baseClaimNum] = (claimCounts[baseClaimNum] || 0) + 1;
          let finalClaimNum = baseClaimNum;
          if (claimCounts[baseClaimNum] > 1) {
            finalClaimNum = `${baseClaimNum}-${claimCounts[baseClaimNum]}`;
          }

          return {
            _isUnsaved: true,
            claim_number: finalClaimNum,
            salvage_item,
            make,
            model,
            serial_number: serial,
            ber_reason: findVal(['berreason', 'reason', 'damage', 'fault', 'condition', 'notes']) || 'Not specified',
            client_name: findVal(['client', 'customer', 'name', 'insured']) || 'Unknown',
            address: findVal(['address', 'location', 'residence', 'city']) || '',
            phone_number: findVal(['phone', 'contact', 'mobile', 'tel', 'cell']) || '',
            status: 'New'
          };
        }).filter(Boolean);

        if (mappedClaims.length === 0) {
          throw new Error('No new claims found. All items in the Excel file were exact duplicates of existing data.');
        }

        setUnsavedClaims(prev => {
          const uniqueMap = new Map();
          prev.forEach(c => uniqueMap.set(c.claim_number, c));
          mappedClaims.forEach(c => uniqueMap.set(c.claim_number, c));
          return Array.from(uniqueMap.values());
        });

      } catch (err) {
        console.error('Import error:', err);
        setError(`Import failed: ${err.message}`);
      } finally {
        setImporting(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };

    reader.onerror = () => {
      setError('Failed to read the file.');
      setImporting(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSaveImports = async () => {
    if (unsavedClaims.length === 0) return;
    
    setSaving(true);
    setError(null);

    try {
      const claimsToSave = unsavedClaims.map((claim) => {
        const { _isUnsaved, salvagerName, ...dbClaim } = claim;
        return dbClaim;
      });

      const chunkSize = 200;
      let insertedCount = 0;

      for (let i = 0; i < claimsToSave.length; i += chunkSize) {
        const chunk = claimsToSave.slice(i, i + chunkSize);
        
        const { error: upsertError } = await supabase
          .from('claims_1783447171616')
          .upsert(chunk, { onConflict: 'claim_number', ignoreDuplicates: false });

        if (upsertError) throw upsertError;
        insertedCount += chunk.length;
      }

      setUnsavedClaims([]);
      fetchClaims();
      alert(`Successfully saved ${insertedCount} claims to the database.`);

    } catch (err) {
      console.error('Save error:', err);
      setError(`Failed to save imports: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const TabButton = ({ id, label, icon, count }) => (
    <button 
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all border-b-2 ${
        activeTab === id 
          ? id === 'rejected' 
            ? 'border-red-500 text-red-600 bg-red-50/50' 
            : 'border-brand text-brand bg-brand/5'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }`}
    >
      <SafeIcon icon={icon} />
      {label}
      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
        activeTab === id 
          ? id === 'rejected' ? 'bg-red-500 text-white' : 'bg-brand text-white'
          : 'bg-gray-200 text-gray-600'
      }`}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 relative">
      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Claims Management</h1>
          <p className="text-gray-500">
            Manage {claims.length} active claims. {unsavedClaims.length > 0 && <span className="text-orange-600 font-medium ml-1">({unsavedClaims.length} unsaved pending)</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium text-sm transition-all shadow-md animate-in fade-in zoom-in duration-200"
            >
              <SafeIcon icon={FiTrash2} /> Delete Selected ({selectedIds.size})
            </button>
          )}

          <button 
            onClick={handleAIDistribute}
            disabled={selectedIds.size === 0 || distributingAI || distributingManual}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium text-sm transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {distributingAI ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiZap} />} 
            Distribute AI
          </button>
          
          <button 
            onClick={() => { fetchSalvagers(); setShowManualModal(true); }}
            disabled={selectedIds.size === 0 || distributingAI || distributingManual}
            className="flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-xl hover:bg-brand font-medium text-sm transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SafeIcon icon={FiUserCheck} /> Distribute Manually
          </button>

          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileUpload} 
            accept=".xlsx, .xls, .csv" 
            className="hidden" 
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={importing || saving}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 font-medium text-sm transition-colors disabled:opacity-50"
          >
            {importing ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiUpload} />}
            {importing ? 'Reading...' : 'Import Excel'}
          </button>

          {unsavedClaims.length > 0 && (
            <button 
              onClick={handleSaveImports}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 font-medium text-sm transition-all shadow-md animate-pulse disabled:animate-none disabled:opacity-50"
            >
              {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
              {saving ? 'Saving to DB...' : `Save ${unsavedClaims.length} Imports`}
            </button>
          )}

          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 font-medium text-sm transition-colors">
            <SafeIcon icon={FiDownload} /> Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-2xl flex items-center gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="text-lg" />
          {error}
        </div>
      )}

      <Card className="overflow-hidden !p-0">
        <div className="flex border-b border-gray-100 bg-white overflow-x-auto">
          <TabButton id="new" label="New Claims" icon={FiBox} count={newCount} />
          <TabButton id="assigned" label="Assigned Claims" icon={FiTruck} count={assignedCount} />
          <TabButton id="accepted" label="Accepted Claims" icon={FiCheckCircle} count={acceptedCount} />
          <TabButton id="rejected" label="Rejected Claims" icon={FiX} count={rejectedCount} />
        </div>

        <div className="p-4 bg-gray-50/50 border-b border-gray-100 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Claim #, Item, Serial, Make, or Assigned Salvager..." 
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand/20 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 text-sm font-medium text-gray-700 bg-white">
              <SafeIcon icon={FiFilter} /> Filters
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100 text-xs uppercase text-gray-400 tracking-wider">
                <th className="px-6 py-4 font-semibold text-center w-12">
                  <input 
                    type="checkbox" 
                    ref={headerCheckboxRef}
                    checked={displayClaims.length > 0 && selectedIds.size === displayClaims.length}
                    onChange={toggleAll}
                    disabled={displayClaims.length === 0}
                    className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand cursor-pointer"
                  />
                </th>
                <th className="px-6 py-4 font-semibold">Claim Number</th>
                <th className="px-6 py-4 font-semibold">Salvage Item</th>
                <th className="px-6 py-4 font-semibold">Make</th>
                <th className="px-6 py-4 font-semibold">Model</th>
                <th className="px-6 py-4 font-semibold">Serial Number</th>
                <th className="px-6 py-4 font-semibold">BER Reason</th>
                <th className="px-6 py-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-50">
              {loading && claims.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-400">
                    <SafeIcon icon={FiLoader} className="animate-spin inline-block mr-2" /> Syncing with Database...
                  </td>
                </tr>
              ) : displayClaims.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-400">
                    {searchQuery 
                      ? 'No claims match your search criteria.' 
                      : `No claims found in ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Claims.`}
                  </td>
                </tr>
              ) : (
                displayClaims.map((claim) => {
                  const claimKey = claim.id || claim.claim_number;
                  const isSelected = selectedIds.has(claimKey);
                  return (
                    <tr 
                      key={claimKey} 
                      className={`transition-colors cursor-pointer group 
                        ${isSelected ? 'bg-brand-accent/50 hover:bg-brand-accent/70' : claim._isUnsaved ? 'bg-orange-50/40 hover:bg-orange-50/80' : 'hover:bg-gray-50/50'}
                        ${contextMenu?.claim?.claim_number === claim.claim_number ? '!bg-brand-accent' : ''}
                      `}
                      onClick={() => handleViewClaim(claim)}
                      onContextMenu={(e) => handleContextMenu(e, claim)}
                    >
                      <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          checked={isSelected}
                          onChange={(e) => toggleSelection(e, claim)}
                          className="w-4 h-4 rounded border-gray-300 text-brand focus:ring-brand cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4 min-w-[200px]">
                        <div className="flex items-center">
                          <span className="font-bold text-gray-900">{claim.claim_number}</span>
                          {claim._isUnsaved ? (
                            <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700 uppercase tracking-wide">
                              Unsaved
                            </span>
                          ) : (
                            <Badge status={claim.status} className="ml-2 scale-75 origin-left">{claim.status}</Badge>
                          )}
                        </div>
                        {claim.salvagerName && !claim._isUnsaved && (
                          <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5 bg-gray-50/80 border border-gray-100 w-max px-2 py-0.5 rounded-md shadow-sm">
                            <SafeIcon icon={FiTruck} size={10} className="text-brand" />
                            <span>Distributed to <span className="font-bold text-gray-700">{claim.salvagerName}</span></span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-medium text-gray-800">{claim.salvage_item || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600">{claim.make}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-500">{claim.model}</span>
                      </td>
                      <td className="px-6 py-4">
                        <code className="text-xs bg-white/50 px-1.5 py-0.5 rounded font-mono text-gray-600 border border-gray-100">
                          {claim.serial_number}
                        </code>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-red-600 font-medium px-2 py-0.5 bg-red-50 rounded-lg text-xs">
                          {claim.ber_reason}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => handleViewClaim(claim)}
                            className="p-2 text-gray-400 hover:text-brand hover:bg-brand-accent rounded-lg transition-colors"
                            title="View Details"
                          >
                            <SafeIcon icon={FiEye} />
                          </button>
                          <button 
                            onClick={() => handleDeleteClaim(claim)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title={claim._isUnsaved ? "Remove Draft" : "Delete Document"}
                          >
                            <SafeIcon icon={FiTrash2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {contextMenu && (
        <div 
          className="fixed bg-white shadow-xl rounded-xl border border-gray-100 py-2 w-48 z-[100] animate-in fade-in zoom-in duration-100"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={() => { handleViewClaim(contextMenu.claim); setContextMenu(null); }}
            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
          >
            <SafeIcon icon={FiEye} className="text-gray-400" /> View Detail
          </button>
          
          <div className="h-px bg-gray-100 my-1 mx-2" />
          
          <button 
            onClick={() => handleDeleteClaim(contextMenu.claim)}
            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 font-medium"
          >
            <SafeIcon icon={FiTrash2} /> {contextMenu.claim._isUnsaved ? 'Remove Draft' : 'Delete Claim'}
          </button>
        </div>
      )}

      {/* Manual Distribution Modal */}
      {showManualModal && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => !distributingManual && setShowManualModal(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-white rounded-3xl shadow-2xl z-50 overflow-hidden flex flex-col max-h-[80vh] animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Distribute Manually</h3>
                <p className="text-sm text-gray-500">Select a salvager for {selectedIds.size} claim(s)</p>
              </div>
              <button 
                onClick={() => !distributingManual && setShowManualModal(false)}
                className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
              >
                <SafeIcon icon={FiX} size={20} />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {salvagersList.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                  <SafeIcon icon={FiUser} size={48} className="mb-3 opacity-20" />
                  <p>No salvagers registered yet.</p>
                </div>
              )}

              {salvagersList.map(s => (
                <div 
                  key={s.id} 
                  onClick={() => !distributingManual && handleManualDistribute(s.id)}
                  className={`p-4 border border-gray-100 rounded-2xl cursor-pointer hover:border-brand hover:bg-brand/5 transition-all flex items-center gap-4 ${distributingManual ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {s.avatar_url ? (
                    <img src={s.avatar_url} alt={s.company_name} className="w-12 h-12 rounded-full object-cover shadow-sm" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                      <SafeIcon icon={FiUser} size={20} />
                    </div>
                  )}
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900">{s.company_name || s.full_name}</h4>
                    <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                      {s.tags?.length > 0 ? s.tags.join(', ') : 'No specialized tags'}
                    </p>
                  </div>
                  <SafeIcon icon={FiChevronRight} className="text-gray-300" />
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <ClaimDrawer 
        claim={selectedClaim} 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        onUpdate={fetchClaims}
      />
    </div>
  );
};